// Multi-select math for the email surfaces (bulk actions). Pure and node-testable —
// the React state lives in components/custom/email/useEmailSelection.ts.
//
// Two rules encoded here, both of which exist because the visible list is derived state
// that moves under the user (pill change, search keystroke, "Load older", the 60s poll):
//
// 1. A shift-click extends from the anchor THROUGH the visible order, and the anchor can
//    legitimately be gone by the time the user shift-clicks. Degrading to a single-row
//    selection is the safe answer — an empty range would silently discard the click, and
//    guessing a replacement anchor would select rows the user never pointed at.
// 2. Selection ids are kept as a Set (cheap membership test per row render), but every
//    consumer needs them in VISIBLE order so a bulk action applies top-down. pruneToVisible
//    does both jobs at once: it intersects with what is on screen and orders the result.

/** Inclusive range between `anchorId` and `targetId` in visible order. Falls back to
 *  `[targetId]` when there is no anchor, or when either endpoint is not visible. */
export function rangeBetween(
  orderedIds: readonly string[],
  anchorId: string | null,
  targetId: string,
): string[] {
  const targetIdx = orderedIds.indexOf(targetId);
  if (targetIdx === -1) return [targetId];
  if (anchorId === null) return [targetId];
  const anchorIdx = orderedIds.indexOf(anchorId);
  if (anchorIdx === -1) return [targetId];
  const start = Math.min(anchorIdx, targetIdx);
  const end = Math.max(anchorIdx, targetIdx);
  return orderedIds.slice(start, end + 1);
}

/** `selected` ∩ `orderedIds`, in visible order. Stale ids (rows that have left the list)
 *  are dropped rather than acted on — the user cannot see them to know they are selected. */
export function pruneToVisible(
  selected: ReadonlySet<string>,
  orderedIds: readonly string[],
): string[] {
  if (selected.size === 0) return [];
  return orderedIds.filter((id) => selected.has(id));
}
